import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { API_ORIGIN } from '../config/env';
import * as s from './publicAuthStyles';

// Auth Phase 2 (P2-B, rulings R10/R11): the two single-use link pages.
//   kind="reset"   → /reset-password/:token → GET/POST /api/auth/reset/:token
//   kind="welcome" → /welcome/:token        → GET/POST /api/auth/invite/:token
// Public, outside the app shell, raw fetch only (the ScanReceipt precedent):
// the token in the URL is the only credential and must never reach the
// session module or the authenticated clients. On load the page checks the
// link (200 valid / 410 expired-or-used); a valid link shows the new-password
// form (12–72 mirror; the server also breach-checks); success shows a
// confirmation with "Go to sign in". No session is created here.
const KINDS = {
  reset: {
    api: 'reset', title: 'Set a new password',
    intro: 'Choose a new password for your GVC Portal account.',
    expired: 'This link has expired or been used.',
    expiredAction: <Link to="/forgot" style={s.secondaryLink}>Request a new link</Link>
  },
  welcome: {
    api: 'invite', title: 'Welcome to the GVC Portal',
    intro: 'Set the password you will use to sign in.',
    expired: 'This invite link has expired or been used.',
    expiredAction: <p style={s.staticNote}>Ask your administrator to send you a new invite.</p>
  }
};

const TokenPasswordPage = ({ kind }) => {
  const cfg = KINDS[kind] || KINDS.reset;
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('checking'); // checking | valid | expired | done | error
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_ORIGIN}/api/auth/${cfg.api}/${encodeURIComponent(token || '')}`);
        if (cancelled) return;
        setState(r.status === 200 ? 'valid' : (r.status === 410 ? 'expired' : 'error'));
      } catch (e) { if (!cancelled) setState('error'); }
    })();
    return () => { cancelled = true; };
  }, [cfg.api, token]);

  const tooShort = password.length > 0 && password.length < s.POLICY_MIN;
  const tooLong = password.length > s.POLICY_MAX;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = !busy && password.length >= s.POLICY_MIN && !tooLong && confirm === password;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setBusy(true);
    try {
      const r = await fetch(`${API_ORIGIN}/api/auth/${cfg.api}/${encodeURIComponent(token || '')}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password })
      });
      const body = await r.json().catch(() => ({}));
      if (r.status === 200) { setState('done'); return; }
      if (r.status === 410) { setState('expired'); return; }
      setError(body.message || (Array.isArray(body.errors) && body.errors[0] && body.errors[0].msg) || `HTTP ${r.status}`);
    } catch (err) {
      setError('Could not reach the server. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={s.page} data-testid={`token-page-${kind}`}>
      <div style={s.card}>
        <img src="/gvc-logo.png" alt="GVC School Bus" style={s.logo} />
        <h1 style={s.title}>{cfg.title}</h1>

        {state === 'checking' && <p style={{ ...s.hint, textAlign: 'center' }}>Checking your link…</p>}

        {state === 'error' && (
          <>
            <p style={s.errorText} role="alert">Could not check this link right now. Please try again in a moment.</p>
            <Link to="/login" style={s.secondaryLink}>Back to sign in</Link>
          </>
        )}

        {state === 'expired' && (
          <>
            <p style={{ ...s.hint, textAlign: 'center', color: '#E0E0E0' }}>{cfg.expired}</p>
            {cfg.expiredAction}
            <Link to="/login" style={s.secondaryLink}>Back to sign in</Link>
          </>
        )}

        {state === 'valid' && (
          <>
            <p style={{ ...s.hint, textAlign: 'center' }}>{cfg.intro}</p>
            <form onSubmit={submit} autoComplete="off">
              <label style={s.label}>New password
                <input style={s.input} type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password" autoFocus disabled={busy} />
              </label>
              <label style={s.label}>Confirm new password
                <input style={s.input} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password" disabled={busy} />
              </label>
              <p style={s.hint}>Your password must be between {s.POLICY_MIN} and {s.POLICY_MAX} characters, and it is checked against known data breaches.</p>
              {tooShort && <p style={s.errorText}>At least {s.POLICY_MIN} characters.</p>}
              {tooLong && <p style={s.errorText}>At most {s.POLICY_MAX} characters.</p>}
              {mismatch && <p style={s.errorText}>Passwords don't match.</p>}
              {error && <p style={s.errorText} role="alert">{error}</p>}
              <button type="submit" style={s.primary} disabled={!canSubmit}>{busy ? 'Saving…' : 'Set password'}</button>
            </form>
          </>
        )}

        {state === 'done' && (
          <>
            <p style={s.successText} role="status">Password set — sign in with your new password.</p>
            <button type="button" style={s.primary} onClick={() => navigate('/login')}>Go to sign in</button>
          </>
        )}
      </div>
    </div>
  );
};

export default TokenPasswordPage;
